import { z } from 'zod';
import { SdkIdentity } from '../ingest/identity.ts';
import { Card } from './cards.ts';

/**
 * Content Card feed sync (BB-66, CARD-006): `POST /sdk/v1/cards/sync`. The SDK sends its cursor; the answer is a delta
 * (new and changed cards, ids to remove) or, with `full: true`, the whole feed — then the SDK replaces its cache. A
 * full answer comes after an identity change, for a missing, broken or foreign cursor, and on schema changes.
 */
export const CARD_SYNC_REASONS = ['session_start', 'feed_open', 'manual', 'identity_change', 'push_hint'] as const;
/** Active cards in one answer at most; pinned first, then the newest. */
export const CARD_FEED_MAX = 250;

export const CardSyncRequest = z.strictObject({
  schemaVersion: z.literal(1),
  installationId: z.uuid(),
  identity: SdkIdentity,
  cursor: z.string().max(2048).nullable(),
  reason: z.enum(CARD_SYNC_REASONS),
  sdk: z.object({ platform: z.enum(['android', 'ios', 'web']), version: z.string().min(1).max(32) }),
});
export type CardSyncRequest = z.infer<typeof CardSyncRequest>;

export const CardSyncResponse = z
  .strictObject({
    schemaVersion: z.literal(1),
    cursor: z.string().min(1).max(2048),
    full: z.boolean(),
    serverTime: z.iso.datetime(),
    cards: z.array(Card).max(CARD_FEED_MAX),
    removedIds: z.array(z.uuid()),
    /** After a manual refresh: when the next one is allowed (token bucket 5 + 1 per 180 s). */
    nextAllowedRefreshAt: z.iso.datetime().optional(),
  })
  .refine((r) => !r.cards.some((c) => r.removedIds.includes(c.id)), { message: 'a card is both sent and removed', path: ['removedIds'] });
export type CardSyncResponse = z.infer<typeof CardSyncResponse>;
